import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LegalDocumentPage } from "@/pages/LegalDocument";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, ShieldAlert, LogOut } from "lucide-react";

/**
 * LegalGate — wraps the authenticated app. On mount (and after login),
 * fetches /legal/status. If the current user has one or more pending
 * documents to accept, we render a full-screen blocking modal showing
 * each pending doc with a single "I have read and agree" checkbox
 * (both docs bundled into the same checkbox — legalese acceptance is
 * typically one affirmative action per gate, not per document).
 *
 * If the user declines (clicks "Log out instead"), we log them out and
 * kick them back to /login. No silent bypass.
 *
 * If nothing is pending, this renders `children` transparently — zero
 * layout impact for the 99% case.
 */
export default function LegalGate({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [pending, setPending] = useState(null);   // null = not yet loaded
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState("terms");  // which doc pane is showing

  // Never gate the public legal pages themselves — they're what the modal
  // links to (target=_blank). Gating them would create a loop where the
  // user can't read the doc the modal tells them to read.
  //
  // Also never gate the billing page. A user must always be able to reach
  // billing and cancel their subscription WITHOUT first accepting new
  // terms — declining new terms cannot trap a paying user in a plan. The
  // cancel flow is entirely in-app (POST /api/billing/cancel, a FREE path
  // not gated by the paywall or any server-side legal check), so allowing
  // the page through is sufficient for cancellation to complete.
  const isBypassRoute =
    location.pathname === "/terms" ||
    location.pathname === "/privacy" ||
    location.pathname === "/billing";

  const refresh = useCallback(async () => {
    if (!user) { setPending([]); return; }
    try {
      const { data } = await api.get("/legal/status");
      setPending(data.pending || []);
    } catch (err) {
      // Non-fatal — if the gate is unreachable, don't lock the user out of
      // the app. Legal enforcement resumes on next status call.
      console.warn("legal/status fetch failed", err);
      setPending([]);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  if (isBypassRoute) return children;
  if (pending === null || pending.length === 0) return children;

  const kinds = pending.map((p) => p.kind);
  const showTerms = kinds.includes("terms");
  const showPrivacy = kinds.includes("privacy");
  // Ensure the tab defaults to whichever doc is pending — if both, keep
  // whatever the user last clicked (initial: terms).
  const effectiveActive = kinds.includes(active) ? active : kinds[0];

  const acceptAll = async () => {
    if (!checked) return;
    setSaving(true);
    try {
      await api.post("/legal/accept", {
        acceptances: pending.map((p) => ({ kind: p.kind, version: p.version })),
      });
      toast.success("Thanks — you're all set.");
      setPending([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const decline = async () => {
    // Users who won't accept can't use the app. Log them out cleanly.
    await logout();
    // AuthContext handles the redirect to /login.
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-3" data-testid="legal-gate">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-4 flex flex-col max-h-[95vh]">
        <div className="p-5 sm:p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-flag-red/10 text-flag-red">
              <ShieldAlert className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-xl sm:text-2xl font-black text-flag-navy" data-testid="legal-gate-title">
                Please review our updated {showTerms && showPrivacy ? "Terms and Privacy Policy" : (showTerms ? "Terms of Service" : "Privacy Policy")}
              </h2>
              <p className="text-sm text-slate-600 mt-0.5">
                We updated the legal documents that govern your use of Electable. Please review and accept the current version to continue.
              </p>
            </div>
          </div>
          {showTerms && showPrivacy && (
            <div className="mt-4 inline-flex bg-slate-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setActive("terms")}
                data-testid="legal-gate-tab-terms"
                className={`px-3 py-1.5 rounded-md text-sm font-bold transition-colors ${effectiveActive === "terms" ? "bg-white text-flag-navy shadow" : "text-slate-600 hover:text-flag-navy"}`}
              >
                Terms of Service
              </button>
              <button
                type="button"
                onClick={() => setActive("privacy")}
                data-testid="legal-gate-tab-privacy"
                className={`px-3 py-1.5 rounded-md text-sm font-bold transition-colors ${effectiveActive === "privacy" ? "bg-white text-flag-navy shadow" : "text-slate-600 hover:text-flag-navy"}`}
              >
                Privacy Policy
              </button>
            </div>
          )}
        </div>

        <div className="p-5 sm:p-6 overflow-y-auto flex-1 min-h-[200px]" data-testid="legal-gate-body">
          <LegalDocumentPage kind={effectiveActive} embedded />
        </div>

        <div className="p-5 sm:p-6 border-t border-slate-200 bg-slate-50 space-y-3">
          <label className="flex items-start gap-2.5 text-sm text-slate-800 cursor-pointer" data-testid="legal-gate-consent-label">
            <Checkbox
              checked={checked}
              onCheckedChange={(v) => setChecked(!!v)}
              className="mt-0.5"
              data-testid="legal-gate-checkbox"
            />
            <span>
              I have read and agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-flag-red font-semibold hover:underline">Terms of Service</a>
              {" "}and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-flag-red font-semibold hover:underline">Privacy Policy</a>.
            </span>
          </label>
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2">
            <Button
              variant="ghost"
              onClick={decline}
              className="text-slate-500 hover:text-flag-red font-semibold"
              data-testid="legal-gate-decline"
            >
              <LogOut className="h-4 w-4 mr-1.5" /> Decline and log out
            </Button>
            <Button
              onClick={acceptAll}
              disabled={!checked || saving}
              className="bg-flag-red hover:bg-flag-red-hover text-white font-bold h-11 px-6"
              data-testid="legal-gate-accept"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              Accept and continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
